import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Megaphone, Send, Trash2, ToggleLeft, ToggleRight, AlertTriangle, Info, AlertCircle } from 'lucide-react';

const priorityConfig = {
  info: { icon: Info, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  urgent: { icon: AlertCircle, bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700' },
};

export default function SchoolAnnouncements() {
  const { t } = useLanguage();
  const [announcements, setAnnouncements] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', priority: 'info', target_type: 'all', class_ids: [] });

  const fetch = async () => {
    try {
      const res = await api.get('/school/announcements');
      setAnnouncements(res.data.data.announcements);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    fetch();
    api.get('/classes').then(r => setClasses(r.data.data || [])).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) { toast.error('Title and message required'); return; }
    if (form.target_type === 'class' && form.class_ids.length === 0) { toast.error('Select at least one class'); return; }
    setSending(true);
    try {
      const res = await api.post('/school/announcements', form);
      toast.success(`Announcement sent to ${res.data.data.notified_users} users`);
      setForm({ title: '', message: '', priority: 'info', target_type: 'all', class_ids: [] });
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const toggleClass = (cid) => {
    setForm(f => ({
      ...f,
      class_ids: f.class_ids.includes(cid) ? f.class_ids.filter(id => id !== cid) : [...f.class_ids, cid]
    }));
  };

  const targetLabel = (a) => {
    if (a.target_type === 'class') {
      return `Class: ${(a.class_ids || []).map(cid => { const c = classes.find(x => x.class_id === cid); return c ? `${c.name} ${c.section}` : cid; }).join(', ')}`;
    }
    if (a.target_type === 'parents') return 'All Parents';
    if (a.target_type === 'staff') return 'Staff Only';
    return 'Everyone';
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/school/announcements/${id}`); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (id) => {
    try { await api.patch(`/school/announcements/${id}/toggle`); fetch(); }
    catch { toast.error('Failed to toggle'); }
  };

  const timeAgo = (d) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const active = announcements.filter(a => a.active);
  const inactive = announcements.filter(a => !a.active);

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md shadow-amber-500/20">
          <Megaphone className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('announcements')}</h1>
          <p className="text-sm text-slate-500">{t('manage_announcements')}</p>
        </div>
      </div>

      <div className="glass-card p-5" data-testid="create-announcement-form">
        <h2 className="text-sm font-bold text-slate-700 mb-4">{t('create_announcement')}</h2>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label className="text-slate-600 text-xs font-semibold">{t('announcement_title')}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. School Holiday Notice" className="bg-white border-slate-200 text-slate-800 rounded-xl" required data-testid="announcement-title-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold">{t('priority')}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="priority-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  <SelectItem value="info">{t('info')}</SelectItem>
                  <SelectItem value="warning">{t('warning')}</SelectItem>
                  <SelectItem value="urgent">{t('urgent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-600 text-xs font-semibold">Send To</Label>
            <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v, class_ids: [] })}>
              <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-800">
                <SelectItem value="all">Everyone (Staff + Parents)</SelectItem>
                <SelectItem value="parents">All Parents Only</SelectItem>
                <SelectItem value="staff">Staff Only</SelectItem>
                <SelectItem value="class">Parents of Specific Class</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.target_type === 'class' && (
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold">Select Classes</Label>
              <div className="flex flex-wrap gap-2">
                {classes.map(c => (
                  <button key={c.class_id} type="button" onClick={() => toggleClass(c.class_id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${form.class_ids.includes(c.class_id) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-400'}`}>
                    {c.name} {c.section}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-slate-600 text-xs font-semibold">{t('announcement_message')}</Label>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} required className="w-full bg-white border border-slate-200 rounded-xl text-slate-800 text-sm p-3 placeholder:text-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10" placeholder="Type your announcement message..." data-testid="announcement-message-input" />
          </div>
          <Button type="submit" className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-xl shadow-sm" disabled={sending} data-testid="publish-btn">
            <Send className="h-4 w-4 mr-2" /> {sending ? t('publishing') : t('publish')}
          </Button>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>
      ) : (
        <>
          {active.length > 0 && (
            <div className="space-y-3" data-testid="active-announcements">
              <h3 className="text-sm font-bold text-slate-700">{t('active_announcements')} ({active.length})</h3>
              {active.map(a => {
                const pc = priorityConfig[a.priority] || priorityConfig.info;
                const Icon = pc.icon;
                return (
                  <div key={a.announcement_id} className={`glass-card p-4 ${pc.bg} border ${pc.border}`} data-testid={`announcement-${a.announcement_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <Icon className={`h-5 w-5 ${pc.text} mt-0.5 flex-shrink-0`} />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className={`text-sm font-bold ${pc.text}`}>{a.title}</h4>
                            <span className="text-[10px] bg-white/70 border border-slate-200 text-slate-500 px-2 py-0.5 rounded-full">{targetLabel(a)}</span>
                          </div>
                          <p className="text-xs text-slate-600 mt-1">{a.message}</p>
                          <p className="text-[10px] text-slate-400 mt-2">{timeAgo(a.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button onClick={() => handleToggle(a.announcement_id)} className="p-1.5 rounded-lg hover:bg-white/50 text-slate-400 hover:text-amber-600 transition-colors" title="Deactivate" data-testid={`toggle-${a.announcement_id}`}>
                          <ToggleRight className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(a.announcement_id)} className="p-1.5 rounded-lg hover:bg-white/50 text-slate-400 hover:text-rose-600 transition-colors" title="Delete" data-testid={`delete-${a.announcement_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {inactive.length > 0 && (
            <div className="space-y-3" data-testid="inactive-announcements">
              <h3 className="text-sm font-bold text-slate-400">{t('past_announcements')} ({inactive.length})</h3>
              {inactive.map(a => (
                <div key={a.announcement_id} className="glass-card p-4 opacity-60">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-slate-500">{a.title}</h4>
                        <span className="text-[10px] bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full">{targetLabel(a)}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{a.message}</p>
                      <p className="text-[10px] text-slate-300 mt-2">{timeAgo(a.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button onClick={() => handleToggle(a.announcement_id)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-teal-600" title="Reactivate">
                        <ToggleLeft className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(a.announcement_id)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-rose-600" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {announcements.length === 0 && (
            <div className="glass-card p-12 text-center" data-testid="no-announcements">
              <Megaphone className="h-12 w-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">{t('no_announcements')}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
